import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import {
  Radio, Video, CalendarPlus, PlayCircle, Users, GraduationCap, Film,
  ShieldCheck, MessageSquare, BarChart3, Clock,
} from "lucide-react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { DashboardWidgetCard, EmptyWidgetState } from "@/components/meeting/DashboardWidgetCard";
import { Sparkline } from "@/components/meeting/Sparkline";
import { useAuth } from "@/context/AuthContext";

function timeAgo(iso) {
  if (!iso) return "";
  const diffMin = Math.max(0, Math.round((Date.now() - new Date(iso).getTime()) / 60000));
  if (diffMin < 1) return "now";
  if (diffMin < 60) return `${diffMin}m`;
  if (diffMin < 1440) return `${Math.round(diffMin / 60)}h`;
  return `${Math.round(diffMin / 1440)}d`;
}

function displayTime(m) {
  const iso = m.scheduled_at || m.started_at || m.created_at;
  return new Date(iso).toLocaleString(undefined, { hour: "numeric", minute: "2-digit", month: "short", day: "numeric" });
}

export default function MeetingDashboardPage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [summary, setSummary] = useState(null);
  const [recordings, setRecordings] = useState(null);
  const [startingClass, setStartingClass] = useState(false);
  const [hostVerification, setHostVerification] = useState(null);
  const isAdmin = user?.role === "admin";

  useEffect(() => {
    api.get("/meetings/dashboard-summary").then((r) => setSummary(r.data)).catch(() => {});
    api.get("/meetings/recordings/mine").then((r) => setRecordings(r.data.slice(0, 5))).catch(() => setRecordings([]));
    if (isAdmin) {
      api.get("/admin/host-verifications").then((r) => setHostVerification(r.data.filter((x) => x.host_verification_status === "pending"))).catch(() => {});
    } else {
      api.get("/meetings/host-verification/me").then((r) => setHostVerification([r.data])).catch(() => {});
    }
  }, [isAdmin]);

  const startLiveClass = async () => {
    if (!isAdmin && !hostVerification?.[0]?.is_verified_host) {
      toast.info("Live Classes need host verification — request access below.");
      navigate("/meetings/admin-approval");
      return;
    }
    setStartingClass(true);
    try {
      const { data } = await api.post("/meetings", { title: "Live Class", meeting_type: "class" });
      navigate(`/meeting/${data.id}`);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Could not start live class");
      setStartingClass(false);
    }
  };

  return (
    <div className="max-w-6xl mx-auto px-5 sm:px-8 py-8" data-testid="meet-dashboard-page">
      <div className="relative overflow-hidden rounded-3xl border border-white/10 bg-gradient-to-br from-[#0d1220] via-[#0b0f1a] to-[#0d1220] p-8 sm:p-10">
        <div className="flex items-center gap-2 mb-6">
          <span className="inline-flex items-center gap-2 text-xs font-bold text-cyan-300 bg-cyan-500/10 border border-cyan-400/20 px-3 py-1.5 rounded-full uppercase tracking-wider" data-testid="meet-live-network-badge">
            <Radio className="w-3 h-3 animate-pulse" /> Live Network · {summary?.live_rooms_count ?? 0} Active Rooms
          </span>
        </div>

        <div className="grid lg:grid-cols-[1fr_auto] gap-8 items-center">
          <div>
            <h1 className="font-heading text-4xl sm:text-5xl font-bold text-white leading-tight">
              Create meetings, host live classes &amp;{" "}
              <span className="bg-gradient-to-r from-cyan-400 to-fuchsia-400 bg-clip-text text-transparent">teach anywhere.</span>
            </h1>
            <p className="text-slate-400 mt-4 max-w-xl">
              Professional video conferencing for interviews, cohort classes, coaching, and enterprise collaboration — 100% peer-to-peer, no download needed.
            </p>
            <div className="flex flex-wrap items-center gap-3 mt-7">
              <button onClick={() => navigate("/meetings/all")} data-testid="meet-hero-new-meeting-btn"
                className="flex items-center gap-2 bg-gradient-to-r from-cyan-500 to-fuchsia-500 hover:opacity-90 text-white text-sm font-semibold px-5 py-3 rounded-xl transition-opacity">
                <Video className="w-4 h-4" /> New meeting
              </button>
              <button onClick={() => navigate("/meetings/all", { state: { autoSchedule: true } })} data-testid="meet-hero-schedule-btn"
                className="flex items-center gap-2 bg-white/5 hover:bg-white/10 border border-white/10 text-white text-sm font-semibold px-5 py-3 rounded-xl transition-colors">
                <CalendarPlus className="w-4 h-4" /> Schedule meeting
              </button>
              <button onClick={startLiveClass} disabled={startingClass} data-testid="meet-hero-start-class-btn"
                className="flex items-center gap-2 bg-amber-500/15 hover:bg-amber-500/25 border border-amber-400/30 text-amber-300 text-sm font-semibold px-5 py-3 rounded-xl transition-colors disabled:opacity-60">
                <PlayCircle className="w-4 h-4" /> {startingClass ? "Starting..." : "Start live class"}
              </button>
            </div>
          </div>
          <div className="hidden lg:block w-52 h-52 rounded-full border border-cyan-400/20 relative shrink-0">
            <div className="absolute inset-6 rounded-full bg-gradient-to-br from-cyan-500 to-fuchsia-500 flex items-center justify-center shadow-lg shadow-cyan-500/30">
              <Video className="w-10 h-10 text-white" />
            </div>
          </div>
        </div>
      </div>

      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4 mt-6">
        <DashboardWidgetCard icon={Video} iconClass="bg-cyan-500/15 text-cyan-300" title="Today's Meetings" footerLabel="View calendar" footerTo="/meetings/calendar" testId="widget-today-meetings">
          {!summary ? <EmptyWidgetState text="Loading..." /> : summary.today_meetings.length === 0 ? <EmptyWidgetState text="No meetings scheduled today." /> : summary.today_meetings.map((m) => (
            <div key={m.id} onClick={() => navigate(`/meeting/${m.id}`)} className="flex items-center justify-between text-sm cursor-pointer hover:bg-white/5 rounded-lg px-2 py-1.5 -mx-2" data-testid={`today-meeting-${m.id}`}>
              <div className="min-w-0">
                <p className="text-white truncate">{m.title}</p>
                <p className="text-xs text-slate-500">{displayTime(m)} · {m.participant_count} people</p>
              </div>
              {m.status === "live" && <span className="text-[10px] font-bold text-red-400 bg-red-400/15 px-2 py-0.5 rounded-full shrink-0">LIVE</span>}
            </div>
          ))}
        </DashboardWidgetCard>

        <DashboardWidgetCard icon={GraduationCap} iconClass="bg-fuchsia-500/15 text-fuchsia-300" title="Upcoming Classes" footerLabel="View all classes" footerTo="/meetings/classes" testId="widget-upcoming-classes">
          {!summary ? <EmptyWidgetState text="Loading..." /> : summary.upcoming_classes.length === 0 ? <EmptyWidgetState text="No classes scheduled yet." /> : summary.upcoming_classes.map((m) => (
            <div key={m.id} onClick={() => navigate(`/meeting/${m.id}`)} className="flex items-center justify-between text-sm cursor-pointer hover:bg-white/5 rounded-lg px-2 py-1.5 -mx-2" data-testid={`upcoming-class-${m.id}`}>
              <div className="min-w-0">
                <p className="text-white truncate">{m.title}</p>
                <p className="text-xs text-slate-500">{displayTime(m)}</p>
              </div>
              <span className="text-xs text-slate-400 flex items-center gap-1 shrink-0"><Users className="w-3 h-3" /> {m.participant_count}</span>
            </div>
          ))}
        </DashboardWidgetCard>

        <DashboardWidgetCard icon={Film} iconClass="bg-violet-500/15 text-violet-300" title="Recent Recordings" footerLabel="Open recordings" footerTo="/meetings/recordings" testId="widget-recent-recordings">
          {!recordings ? <EmptyWidgetState text="Loading..." /> : recordings.length === 0 ? <EmptyWidgetState text="No recordings yet — start a meeting and hit the record button to capture your first session." /> : recordings.map((r) => (
            <div key={r.id} onClick={() => navigate(`/meetings/recordings/${r.id}`)} className="flex items-center justify-between text-sm cursor-pointer hover:bg-white/5 rounded-lg px-2 py-1.5 -mx-2" data-testid={`recent-recording-${r.id}`}>
              <div className="min-w-0">
                <p className="text-white truncate">{r.title}</p>
                <p className="text-xs text-slate-500 truncate">{r.meeting_title}</p>
              </div>
              <span className="text-xs text-slate-500 shrink-0">{timeAgo(r.created_at)}</span>
            </div>
          ))}
        </DashboardWidgetCard>

        <DashboardWidgetCard icon={ShieldCheck} iconClass="bg-orange-500/15 text-orange-300" title="Pending Approvals" footerLabel="Open admin panel" footerTo="/meetings/admin-approval" testId="widget-pending-approvals">
          {!hostVerification ? <EmptyWidgetState text="Loading..." /> : isAdmin ? (
            hostVerification.length === 0 ? <EmptyWidgetState text="No pending host verification requests." /> : (
              <div data-testid="pending-approvals-count" className="flex items-center gap-2 text-sm text-white">
                <span className="text-2xl font-bold text-orange-300">{hostVerification.length}</span>
                <span className="text-slate-400">host verification request{hostVerification.length === 1 ? "" : "s"} awaiting review</span>
              </div>
            )
          ) : hostVerification[0]?.is_verified_host ? (
            <p className="text-sm text-emerald-300">You're a verified host — you can create Live Classes.</p>
          ) : hostVerification[0]?.host_verification_status === "pending" ? (
            <p className="text-sm text-amber-300">Your Live Class host request is pending review.</p>
          ) : (
            <EmptyWidgetState text="Request verification to unlock Live Class hosting." />
          )}
        </DashboardWidgetCard>

        <DashboardWidgetCard icon={MessageSquare} iconClass="bg-teal-500/15 text-teal-300" title="Recent Chats" footerLabel="Open chat" footerTo="/meetings/chat" testId="widget-recent-chats">
          {!summary ? <EmptyWidgetState text="Loading..." /> : summary.recent_chats.length === 0 ? <EmptyWidgetState text="No chat messages yet." /> : summary.recent_chats.map((c) => (
            <div key={c.meeting_id} onClick={() => navigate("/meetings/chat")} className="flex items-center justify-between text-sm cursor-pointer hover:bg-white/5 rounded-lg px-2 py-1.5 -mx-2" data-testid={`recent-chat-${c.meeting_id}`}>
              <div className="min-w-0">
                <p className="text-white truncate">{c.title}</p>
                <p className="text-xs text-slate-500 truncate">{c.last_sender}: {c.last_message}</p>
              </div>
              <span className="text-xs text-slate-500 shrink-0">{timeAgo(c.last_sent_at)}</span>
            </div>
          ))}
        </DashboardWidgetCard>

        <DashboardWidgetCard icon={BarChart3} iconClass="bg-cyan-500/15 text-cyan-300" title="Analytics" footerLabel="Open analytics" footerTo="/meetings/analytics" testId="widget-analytics">
          {!summary ? <EmptyWidgetState text="Loading..." /> : (
            <div data-testid="widget-analytics-content">
              <p className="text-2xl font-bold text-white">{summary.analytics.total_minutes_this_week}<span className="text-sm text-slate-500 ml-1">min</span></p>
              <p className="text-xs text-slate-500 mb-2 flex items-center gap-1"><Clock className="w-3 h-3" /> Meeting minutes this week</p>
              <Sparkline data={summary.analytics.daily_breakdown.map((d) => d.minutes)} />
            </div>
          )}
        </DashboardWidgetCard>
      </div>
    </div>
  );
}
